import { addDays, compareOpportunities, isPast, localDate, type Opportunity, type WallClock } from '../../shared/opportunity';
import {
  SWITCHES,
  TIME_SLOTS,
  WEEKDAYS,
  timeSlotOf,
  weekdayOf,
  type SwitchKey,
  type TimeSlot,
  type Weekday,
} from '../../shared/taxonomy';
import { makeTravelTimes, type TravelTimes } from '../travel/travelTimes';
import { DISTANCE_STEPS, DEFAULT_MODE, type LatLng, type TravelCity, type TravelMode } from '../travel/walkshed';

/**
 * Explore's filter engine (contract §5): every filter, count and suggestion,
 * worked out in the browser over the city file.
 *
 * Synchronous and pure. Filtering ~1,000 records costs about a millisecond, so
 * everything is recomputed on every change: no caching, no API.
 *
 * Three pools widen or narrow what the filters run over rather than being
 * filters themselves: past events, events marked Not Interested, and the scope
 * (contract §10): Saved runs the same filters over Going and Maybe only, as
 * `savedOnly`. The Scope pill sets it; it is no longer a switch in the panel,
 * so it is neither counted as a filter nor offered as one to drop. The tri-state switches filter to `true` only; a record
 * that doesn't say is excluded and counted (`unknownExcluded`), never read as "no".
 */

export { DISTANCE_STEPS };

export interface FilterState {
  query: string;
  /** Applies to every date in the window. */
  weekdays: Set<Weekday>;
  slots: Set<TimeSlot>;
  /** Role keys. */
  roles: Set<string>;
  /** Topic keys. */
  topics: Set<string>;
  switches: Set<SwitchKey>;
  /** At most this many minutes from the origin, one of `DISTANCE_STEPS`; null is any distance. */
  withinMinutes: number | null;
  /**
   * Which point distance is measured from; Current falls back to home until a
   * position arrives. Address is a searched place for this session only, never
   * written to the address bar: without it (a reload) it is home again.
   */
  origin: 'home' | 'current' | 'address';
  mode: TravelMode;
  sort: 'soonest' | 'nearest';
  /** Going or Maybe only: the Saved scope, set by the Scope pill rather than the panel. */
  savedOnly: boolean;
  showHidden: boolean;
  showPast: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  query: '',
  weekdays: new Set(),
  slots: new Set(),
  roles: new Set(),
  topics: new Set(),
  switches: new Set(),
  withinMinutes: null,
  origin: 'home',
  mode: DEFAULT_MODE,
  sort: 'soonest',
  savedOnly: false,
  showHidden: false,
  showPast: false,
};

/** What the browser holds that the filters do not name. */
export interface ListContext {
  /** Marked Not Interested. */
  hiddenIds: ReadonlySet<string>;
  /** Going or Maybe. */
  rsvpIds: ReadonlySet<string>;
  /** The user's edited copies, which replace the listing everywhere. */
  editedById: ReadonlyMap<string, Opportunity>;
  /** Where distance is measured from (home, or the device); null disables the distance filter and nearest-first. */
  origin: LatLng | null;
  /** The city's neighborhoods and speeds; null until the file has loaded. */
  city: TravelCity | null;
  /**
   * Minutes from `origin` to each event, from TravelTime where it has answered
   * (contract §17). Without it the geometric estimate is used, as it is when
   * TravelTime fails. Must be for the filters' own mode.
   */
  travel?: TravelTimes | null;
  now: WallClock;
  /** Day keys, inclusive: today .. today + 56 by default. */
  window: { from: string; to: string };
}

/** The rolling window the demo lists: today and the eight weeks after it. */
export const WINDOW_DAYS = 56;
export const defaultWindow = (now: WallClock) => ({ from: now.dayKey, to: addDays(now.dayKey, WINDOW_DAYS) });

/** One entry in the list: the next occurrence of a series (or a one-off), and the rest of its dates. */
export interface Card {
  lead: Opportunity;
  /** Later occurrences that also match, soonest first. */
  more: Opportunity[];
}

export interface FilterResult {
  /** Every matching record, in list order. */
  items: Opportunity[];
  /** Per active switch: how many cards it left out because the listing doesn't say. */
  unknownExcluded: Partial<Record<SwitchKey, number>>;
  /** With a distance limit on: matches whose travel time can't be worked out, shown in their own group at the end. */
  unknownDistance: Opportunity[];
}

export function activeFilterCount(f: FilterState): number {
  return (
    f.weekdays.size +
    f.slots.size +
    f.roles.size +
    f.topics.size +
    f.switches.size +
    (f.withinMinutes != null ? 1 : 0) +
    (f.query.trim() ? 1 : 0)
  );
}

export function hasAnyFilter(f: FilterState): boolean {
  return activeFilterCount(f) > 0;
}

/** A stable signature of the filter state, for keys and effects. */
export function filterKey(f: FilterState): string {
  return [
    f.query.trim().toLowerCase(),
    [...f.weekdays].sort().join(','),
    [...f.slots].sort().join(','),
    [...f.roles].sort().join(','),
    [...f.topics].sort().join(','),
    [...f.switches].sort().join(','),
    f.withinMinutes ?? '',
    f.origin,
    f.mode,
    f.sort,
    f.savedOnly ? 'saved' : '',
    f.showHidden ? 'hidden' : '',
    f.showPast ? 'past' : '',
  ].join('|');
}

// --- one record against the filters ---------------------------------------------

/** Case- and accent-insensitive. */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export const searchTokens = (query: string): string[] => fold(query.trim()).split(/\s+/).filter(Boolean);

const haystacks = new WeakMap<Opportunity, string>();

function haystack(e: Opportunity): string {
  let text = haystacks.get(e);
  if (text == null) {
    text = fold(
      [
        e.name,
        e.summary,
        e.description,
        e.organizationName,
        e.sourceName,
        ...e.topics.map((t) => t.label),
        ...e.roles.map((r) => r.label),
        e.address,
        e.neighborhood,
        e.eventType,
      ]
        .filter(Boolean)
        .join(' '),
    );
    haystacks.set(e, text);
  }
  return text;
}

/** A switch's answer for one record: yes, no, or the listing doesn't say. */
export function switchValue(e: Opportunity, key: SwitchKey): boolean | null {
  switch (key) {
    case 'inPerson':
      return e.format === 'see_listing' ? null : e.format === 'in_person' || e.format === 'hybrid';
    case 'virtual':
      return e.format === 'see_listing' ? null : e.format === 'virtual' || e.format === 'hybrid';
    case 'indoor':
      return e.isOutdoor == null ? null : !e.isOutdoor;
    case 'outdoor':
      return e.isOutdoor;
    case 'free':
      return e.isFree;
    case 'accessible':
      return e.isAccessible;
    case 'noExperience':
      return e.noExperienceNeeded;
    case 'under18':
      // Unstated ages stay in, with a warning chip: the spec's rule.
      return e.minAge == null || e.minAge < 18;
  }
}

/** Under 18 is on and the listing doesn't state an age: the ticket warns. */
export const ageUnstated = (e: Opportunity, f: FilterState): boolean => f.switches.has('under18') && e.minAge == null;

const anyOf = (values: string[], selected: ReadonlySet<string>) =>
  selected.size === 0 || values.some((v) => selected.has(v));

/** Everything a record passes, facet by facet, so counts can leave one facet out. */
interface Checks {
  pool: boolean;
  query: boolean;
  weekdays: boolean;
  slots: boolean;
  roles: boolean;
  topics: boolean;
  /** The active switches this record fails, and those it fails for want of data. */
  failedSwitches: SwitchKey[];
  unknownSwitches: SwitchKey[];
  /**
   * Minutes from the origin, or null when it can't be worked out; undefined
   * without an origin. `UNREACHABLE` (Infinity) is known to be beyond reach.
   */
  minutes: number | null | undefined;
}

function inPool(e: Opportunity, f: FilterState, ctx: ListContext): boolean {
  const day = localDate(e);
  if (day > ctx.window.to) return false;
  if (!f.showPast && (isPast(e, ctx.now) || day < ctx.window.from)) return false;
  if (!f.showHidden && ctx.hiddenIds.has(e.id)) return false;
  if (f.savedOnly && !ctx.rsvpIds.has(e.id)) return false;
  return true;
}

/** The minutes the filters read: TravelTime's when given, else the geometric estimate. */
function travelTimesFor(f: FilterState, ctx: ListContext): TravelTimes | null {
  if (!ctx.origin || !ctx.city) return null;
  if (ctx.travel && ctx.travel.mode === f.mode) return ctx.travel;
  return makeTravelTimes({ origin: ctx.origin, mode: f.mode, city: ctx.city });
}

function check(e: Opportunity, f: FilterState, ctx: ListContext, tokens: string[], times: TravelTimes | null): Checks {
  const failedSwitches: SwitchKey[] = [];
  const unknownSwitches: SwitchKey[] = [];
  for (const { key } of SWITCHES) {
    const value = switchValue(e, key);
    if (value !== true) {
      failedSwitches.push(key);
      if (value == null) unknownSwitches.push(key);
    }
  }
  const slot = timeSlotOf(e);
  return {
    pool: inPool(e, f, ctx),
    query: tokens.length === 0 || tokens.every((t) => haystack(e).includes(t)),
    weekdays: f.weekdays.size === 0 || f.weekdays.has(weekdayOf(localDate(e))),
    slots: f.slots.size === 0 || (slot != null && f.slots.has(slot)),
    roles: anyOf(e.roles.map((r) => r.key), f.roles),
    topics: anyOf(e.topics.map((t) => t.key), f.topics),
    failedSwitches,
    unknownSwitches,
    minutes: times ? times.minutes(e) : undefined,
  };
}

const switchesPass = (c: Checks, active: ReadonlySet<SwitchKey>, except?: SwitchKey) =>
  !c.failedSwitches.some((k) => k !== except && active.has(k));

/** Within the limit, or no limit. A record with no travel time is its own case (`unknownDistance`). */
function withinPasses(c: Checks, within: number | null): boolean | null {
  if (within == null || c.minutes === undefined) return true;
  if (c.minutes === null) return null;
  return c.minutes <= within;
}

type Facet = 'query' | 'weekdays' | 'slots' | 'roles' | 'topics' | 'switches' | 'within';

/** Passes every facet but `skip`. Distance-unknown records pass here; `applyFilters` sets them aside. */
function passesExcept(c: Checks, f: FilterState, skip?: Facet): boolean {
  return (
    c.pool &&
    (skip === 'query' || c.query) &&
    (skip === 'weekdays' || c.weekdays) &&
    (skip === 'slots' || c.slots) &&
    (skip === 'roles' || c.roles) &&
    (skip === 'topics' || c.topics) &&
    (skip === 'switches' || switchesPass(c, f.switches)) &&
    (skip === 'within' || withinPasses(c, f.withinMinutes) !== false)
  );
}

/** The listings as the user sees them: their edits applied. */
const edited = (all: Opportunity[], ctx: ListContext) =>
  ctx.editedById.size === 0 ? all : all.map((e) => ctx.editedById.get(e.id) ?? e);

function checkAll(all: Opportunity[], f: FilterState, ctx: ListContext) {
  const tokens = searchTokens(f.query);
  const times = travelTimesFor(f, ctx);
  return edited(all, ctx).map((e) => ({ e, c: check(e, f, ctx, tokens, times) }));
}

// --- the list -------------------------------------------------------------------

/** Nearest first: known minutes, then beyond reach, then unknown; ties go to the clock. */
function compareMinutes(a: number | null | undefined, b: number | null | undefined): number {
  const rank = (m: number | null | undefined) => (m == null ? 2 : m === Infinity ? 1 : 0);
  return rank(a) - rank(b) || (rank(a) === 0 ? (a as number) - (b as number) : 0);
}

function sortItems(items: { e: Opportunity; c: Checks }[], f: FilterState): Opportunity[] {
  if (f.sort === 'nearest' && items.some(({ c }) => c.minutes != null)) {
    return [...items]
      .sort((a, b) => compareMinutes(a.c.minutes, b.c.minutes) || compareOpportunities(a.e, b.e))
      .map(({ e }) => e);
  }
  return items.map(({ e }) => e).sort(compareOpportunities);
}

/** The key a card is counted under: one per series. */
const cardKey = (e: Opportunity) => e.seriesId ?? e.id;

const countCards = (records: Iterable<Opportunity>) => {
  const keys = new Set<string>();
  for (const e of records) keys.add(cardKey(e));
  return keys.size;
};

export function applyFilters(all: Opportunity[], f: FilterState, ctx: ListContext): FilterResult {
  const checked = checkAll(all, f, ctx);
  const matched = checked.filter(({ c }) => passesExcept(c, f));

  const unknownExcluded: Partial<Record<SwitchKey, number>> = {};
  for (const key of f.switches) {
    const left = checked.filter(
      ({ c }) =>
        passesExcept(c, f, 'switches') &&
        switchesPass(c, f.switches, key) &&
        c.unknownSwitches.includes(key),
    );
    unknownExcluded[key] = countCards(left.map(({ e }) => e));
  }

  const known = matched.filter(({ c }) => withinPasses(c, f.withinMinutes) === true);
  const unknown = matched.filter(({ c }) => withinPasses(c, f.withinMinutes) === null);

  return {
    items: sortItems(known, f),
    unknownExcluded,
    unknownDistance: unknown.map(({ e }) => e).sort(compareOpportunities),
  };
}

/**
 * One card per series: its next matching occurrence leads, and the rest follow
 * as "+N more dates". Cards keep the order their leads have in `items`.
 */
export function collapseSeries(items: Opportunity[]): Card[] {
  const cards: Card[] = [];
  const bySeries = new Map<string, Card>();
  const soonest = [...items].sort(compareOpportunities);
  const leadOf = new Map<string, Opportunity>();
  for (const e of soonest) if (e.seriesId && !leadOf.has(e.seriesId)) leadOf.set(e.seriesId, e);

  for (const e of items) {
    if (!e.seriesId) {
      cards.push({ lead: e, more: [] });
      continue;
    }
    if (bySeries.has(e.seriesId)) continue;
    const card: Card = { lead: leadOf.get(e.seriesId) as Opportunity, more: [] };
    bySeries.set(e.seriesId, card);
    cards.push(card);
  }
  for (const e of soonest) {
    const card = e.seriesId ? bySeries.get(e.seriesId) : undefined;
    if (card && card.lead !== e) card.more.push(e);
  }
  return cards;
}

// --- counts ---------------------------------------------------------------------

export interface FacetCounts {
  weekdays: Record<Weekday, number>;
  slots: Record<TimeSlot, number>;
  roles: Record<string, number>;
  topics: Record<string, number>;
  switches: Record<SwitchKey, number>;
  /**
   * Per step of `DISTANCE_STEPS`, and `any` for no limit: the slider's "41
   * events within 30 min". Zero at every step without an origin.
   */
  withinMinutes: Record<number | 'any', number>;
  /** How many cards turning each pool toggle on would show. */
  savedOnly: number;
  showHidden: number;
  showPast: number;
}

/**
 * Beside every option: how many cards choosing it would show, given every
 * other facet as it stands (a facet never counts against itself). Counted on
 * the collapsed view, one per series, so the numbers match the list.
 */
export function facetCounts(all: Opportunity[], f: FilterState, ctx: ListContext): FacetCounts {
  const checked = checkAll(all, f, ctx);
  const tally = <K extends string>(keys: K[], skip: Facet, has: (e: Opportunity, c: Checks, key: K) => boolean) => {
    const base = checked.filter(({ c }) => passesExcept(c, f, skip));
    const out = {} as Record<K, number>;
    for (const key of keys) out[key] = countCards(base.filter(({ e, c }) => has(e, c, key)).map(({ e }) => e));
    return out;
  };

  const roleKeys = [...new Set(checked.flatMap(({ e }) => e.roles.map((r) => r.key)))];
  const topicKeys = [...new Set(checked.flatMap(({ e }) => e.topics.map((t) => t.key)))];

  const switches = {} as Record<SwitchKey, number>;
  for (const { key } of SWITCHES) {
    switches[key] = countCards(
      checked
        .filter(({ c }) => passesExcept(c, f, 'switches') && switchesPass(c, f.switches, key) && !c.failedSwitches.includes(key))
        .map(({ e }) => e),
    );
  }

  const withinBase = checked.filter(({ c }) => passesExcept(c, f, 'within'));
  const withinMinutes = { any: countCards(withinBase.map(({ e }) => e)) } as Record<number | 'any', number>;
  for (const step of DISTANCE_STEPS) {
    withinMinutes[step] = countCards(
      withinBase.filter(({ c }) => c.minutes != null && c.minutes <= step).map(({ e }) => e),
    );
  }

  const poolCount = (next: FilterState) => {
    const again = checkAll(all, next, ctx);
    return countCards(again.filter(({ c }) => passesExcept(c, next)).map(({ e }) => e));
  };

  return {
    weekdays: tally(WEEKDAYS.map((d) => d.key), 'weekdays', (e, _c, key) => weekdayOf(localDate(e)) === key),
    slots: tally(TIME_SLOTS.map((s) => s.id), 'slots', (e, _c, key) => timeSlotOf(e) === key),
    roles: tally(roleKeys, 'roles', (e, _c, key) => e.roles.some((r) => r.key === key)),
    topics: tally(topicKeys, 'topics', (e, _c, key) => e.topics.some((t) => t.key === key)),
    switches,
    withinMinutes,
    savedOnly: poolCount({ ...f, savedOnly: true }),
    showHidden: f.showHidden ? 0 : poolCount({ ...f, showHidden: true }) - poolCount(f),
    showPast: f.showPast ? 0 : poolCount({ ...f, showPast: true }) - poolCount(f),
  };
}

// --- what to loosen ---------------------------------------------------------------

export type FacetKey =
  | 'weekdays'
  | 'slots'
  | 'roles'
  | 'topics'
  | 'switches'
  | 'withinMinutes'
  | 'query'
  | 'savedOnly';

export interface LoosenSuggestion {
  facet: FacetKey;
  value: string;
  /** What to drop, e.g. "Outdoor". */
  label: string;
  /** How many events come back if it is dropped. */
  gain: number;
}

export function relax(f: FilterState, facet: FacetKey, value: string): FilterState {
  const next: FilterState = {
    ...f,
    weekdays: new Set(f.weekdays),
    slots: new Set(f.slots),
    roles: new Set(f.roles),
    topics: new Set(f.topics),
    switches: new Set(f.switches),
  };
  switch (facet) {
    case 'weekdays': next.weekdays.delete(value as Weekday); break;
    case 'slots': next.slots.delete(value as TimeSlot); break;
    case 'roles': next.roles.delete(value); break;
    case 'topics': next.topics.delete(value); break;
    case 'switches': next.switches.delete(value as SwitchKey); break;
    case 'withinMinutes': next.withinMinutes = null; break;
    case 'query': next.query = ''; break;
    case 'savedOnly': next.savedOnly = false; break;
  }
  return next;
}

export interface Labels {
  roleLabel: (key: string) => string;
  topicLabel: (key: string) => string;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * "Drop Outdoor to see 41 events." The one active selection whose removal
 * brings back the most cards, or null when none brings any back.
 */
export function findLoosenSuggestion(
  all: Opportunity[],
  f: FilterState,
  ctx: ListContext,
  labels: Labels = { roleLabel: (k) => k, topicLabel: (k) => k },
): LoosenSuggestion | null {
  const candidates: { facet: FacetKey; value: string; label: string }[] = [];
  for (const v of f.weekdays) candidates.push({ facet: 'weekdays', value: v, label: WEEKDAYS.find((d) => d.key === v)?.weekday ?? v });
  for (const v of f.slots) candidates.push({ facet: 'slots', value: v, label: capitalise(v) });
  for (const v of f.roles) candidates.push({ facet: 'roles', value: v, label: labels.roleLabel(v) });
  for (const v of f.topics) candidates.push({ facet: 'topics', value: v, label: labels.topicLabel(v) });
  for (const v of f.switches) candidates.push({ facet: 'switches', value: v, label: SWITCHES.find((s) => s.key === v)?.label ?? v });
  if (f.withinMinutes != null) candidates.push({ facet: 'withinMinutes', value: String(f.withinMinutes), label: `Within ${f.withinMinutes} min` });
  if (f.query.trim()) candidates.push({ facet: 'query', value: f.query, label: `“${f.query.trim()}”` });

  let best: LoosenSuggestion | null = null;
  for (const c of candidates) {
    const result = applyFilters(all, relax(f, c.facet, c.value), ctx);
    const gain = countCards([...result.items, ...result.unknownDistance]);
    if (gain > 0 && (!best || gain > best.gain)) best = { ...c, gain };
  }
  return best;
}

/** Whether one event belongs in the list: the edit form's preview asks this. */
export function matchesLocally(e: Opportunity, f: FilterState, ctx: ListContext): boolean {
  return passesExcept(check(e, f, ctx, searchTokens(f.query), travelTimesFor(f, ctx)), f);
}

/** Every upcoming date of a series, soonest first: the callout's Dates list. */
export function seriesDates(all: Iterable<Opportunity>, seriesId: string, now: WallClock): Opportunity[] {
  const out: Opportunity[] = [];
  for (const e of all) if (e.seriesId === seriesId && !isPast(e, now)) out.push(e);
  return out.sort(compareOpportunities);
}
